<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'dashboard/components-next/dropdown-menu/base';

// CRM-01h fase 3: a barra do "mover em bloco", entre o cabeçalho e o quadro. Aparece com qualquer
// seleção; enquanto o lote roda, mostra o progresso no lugar dos botões.
const props = defineProps({
  count: { type: Number, default: 0 },
  stages: { type: Array, default: () => [] },
  // O lote, como o servidor devolve: { status, total, moved, skipped, errors }.
  progress: { type: Object, default: null },
});

const emit = defineEmits(['moveTo', 'clear']);

const { t } = useI18n();

const rodando = computed(() => props.progress?.status === 'running');

// Pulado também é card resolvido: o lote andou por ele.
const andados = computed(
  () => (props.progress?.moved || 0) + (props.progress?.skipped || 0)
);

const percentual = computed(() =>
  props.progress?.total
    ? Math.min(100, Math.round((andados.value / props.progress.total) * 100))
    : 0
);
</script>

<template>
  <div
    class="flex flex-wrap items-center px-4 py-2 border border-t-0 gap-x-3 gap-y-2 bg-n-alpha-1 border-n-weak"
  >
    <template v-if="rodando">
      <span class="text-sm font-medium text-n-slate-12 tabular-nums">
        {{ t('KANBAN.BULK.MOVING', { moved: andados, total: progress.total }) }}
      </span>
      <div
        class="flex-1 min-w-[120px] h-1.5 overflow-hidden rounded-full bg-n-alpha-3"
      >
        <div
          class="h-full transition-all rounded-full bg-n-brand"
          :style="{ width: `${percentual}%` }"
        />
      </div>
    </template>
    <template v-else>
      <span class="text-sm font-medium text-n-slate-12 tabular-nums">
        {{ t('KANBAN.BULK.SELECTED', { count }, count) }}
      </span>
      <DropdownContainer>
        <template #trigger="{ toggle, isOpen }">
          <Button
            :label="t('KANBAN.BULK.MOVE_TO')"
            icon="i-lucide-arrow-right-left"
            size="sm"
            :aria-expanded="isOpen"
            @click="toggle"
          />
        </template>
        <DropdownBody class="z-50 mt-1 min-w-56">
          <DropdownSection class="[&>ul]:grid-cols-1">
            <DropdownItem
              v-for="stage in stages"
              :key="stage.id"
              class="cursor-pointer"
              @click="emit('moveTo', stage)"
            >
              <template #label>
                <span
                  class="flex-shrink-0 rounded-full size-2.5"
                  :style="{ backgroundColor: stage.color }"
                />
                <span class="min-w-0 truncate text-n-slate-12">
                  {{ stage.name }}
                </span>
              </template>
            </DropdownItem>
          </DropdownSection>
        </DropdownBody>
      </DropdownContainer>
      <button
        type="button"
        class="text-sm text-n-slate-11 hover:text-n-slate-12 hover:underline"
        @click="emit('clear')"
      >
        {{ t('KANBAN.BULK.CLEAR') }}
      </button>
    </template>
  </div>
</template>
